import {
  ALPHABET,
  letterIndex,
  lettersOnly,
  mod,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  type CipherDirection,
  type KeyedRun,
  runKeyedLetters,
} from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";

/**
 * The Porta cipher, in the form the American Cryptogram Association publishes.
 *
 * It has 13 alphabets, one for each *pair* of key letters (A,B · C,D · … · Y,Z). Take
 * the pair's number j = ⌊key ÷ 2⌋ (A,B = 0 … Y,Z = 12) and number the letters A = 0 …
 * Z = 25. Then:
 *
 * - a plain letter in the first half (A–M, index p < 13) becomes N–Z:
 *   C = 13 + ((p + j) mod 13);
 * - a plain letter in the second half (N–Z, index p ≥ 13) becomes A–M:
 *   C = (p − 13 − j) mod 13.
 *
 * Every alphabet swaps the two halves of the alphabet, so each is its own inverse — the
 * cipher is reciprocal, and decoding is the same operation as encoding. The ACA's own
 * example is the test vector: key PORTA turns "encipherment is reciprocal" into
 * YGXRC OYJVR GMQJE YWQGE HWVU.
 */

const HALF = 13;

/** The Porta alphabet a key letter selects: 0 for A,B up to 12 for Y,Z. */
export function portaRow(keyIndex: number): number {
  return Math.floor(keyIndex / 2);
}

/** The label of a Porta alphabet row — "A,B" for 0, "C,D" for 1 … */
export function portaRowLabel(row: number): string {
  return `${ALPHABET.charAt(row * 2)},${ALPHABET.charAt(row * 2 + 1)}`;
}

/** The letter a Porta alphabet turns a letter into; applying it twice gives the letter back. */
export function portaSubstitute(keyIndex: number, letter: number): number {
  const row = portaRow(keyIndex);
  if (letter < HALF) {
    return HALF + mod(letter + row, HALF);
  }
  return mod(letter - HALF - row, HALF);
}

/** One step's arithmetic, as it is written in the working table. */
export function describePorta(keyIndex: number, letter: number): string {
  const row = portaRow(keyIndex);
  if (letter < HALF) {
    const sum = letter + row;
    const reduced = sum % HALF;
    const wrapped = sum === reduced ? "" : ` ≡ ${reduced}`;
    return `${letter} + ${row} = ${sum}${wrapped} → 13 + ${reduced} = ${HALF + reduced}`;
  }
  const diff = letter - HALF - row;
  const shown = diff < 0 ? `−${-diff}` : `${diff}`;
  const reduced = mod(diff, HALF);
  const wrapped = diff === reduced ? "" : ` ≡ ${reduced}`;
  return `(${letter} − 13) − ${row} = ${shown}${wrapped}`;
}

export interface PortaResult extends KeyedRun {
  /** The keyword's letters in capitals — empty when the keyword has none. */
  key: string;
}

export function portaTransform(
  text: string,
  keyword: string,
  direction: CipherDirection
): PortaResult {
  const key = lettersOnly(keyword);
  if (key === "") {
    return { key, output: "", steps: [] };
  }
  const run = runKeyedLetters(text, direction, (letter, position) => {
    const keyLetter = key.charAt(position % key.length);
    const keyIndex = letterIndex(keyLetter);
    const value = letterIndex(letter);
    const result = ALPHABET.charAt(portaSubstitute(keyIndex, value));
    const row = portaRow(keyIndex);
    const calc = {
      left: value,
      op: "+" as const,
      right: row,
      raw: value + row,
    };
    // The alphabets are reciprocal, so the pair is the same either way round.
    return direction === "encode"
      ? { plain: letter, key: keyLetter, cipher: result, calc }
      : { plain: result, key: keyLetter, cipher: letter, calc };
  });
  return { key, ...run };
}

/** The 13 alphabets, row by row: row j lists the cipher letter for A … Z. */
export function portaSquare(): string[] {
  const cells: string[] = [];
  for (let key = 0; key < ALPHABET.length; key += 2) {
    for (let letter = 0; letter < ALPHABET.length; letter += 1) {
      cells.push(ALPHABET.charAt(portaSubstitute(key, letter)));
    }
  }
  return cells;
}
